import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type UseMetricChartResult, useMetricChart } from '@/components/charts/generic';
import type { ThresholdRange, TiltDataPoint } from '@/domain/behavior';
import { parseLocale } from '@/utils/localeFormatters';

import { toBehaviorContext } from '../contextSeries/contextSeries';

import { buildTiltSpec } from './tiltChartSpec';

interface UseTiltChartDataParams {
    tiltDataPoints: TiltDataPoint[];
    tiltThreshold: ThresholdRange;
}

export interface UseTiltChartDataReturn {
    chart: UseMetricChartResult<'line'>;
    hasOxygenData: boolean;
    /** The radio group's value: 'temperature', 'depth' or 'oxygen'. */
    selectedRightAxisValue: string;
    setSelectedRightAxisValue: (value: string) => void;
}

/** The tilt page's chart, with the reading on its right axis picked by a radio group. */
export function useTiltChartData({
    tiltDataPoints,
    tiltThreshold,
}: UseTiltChartDataParams): UseTiltChartDataReturn {
    const { t, i18n } = useTranslation('behaviorModule');
    const [choice, setChoice] = useState('temperature');

    const hasOxygenData = useMemo(
        () => tiltDataPoints.some((point) => point.oxygen != null),
        [tiltDataPoints]
    );
    const context = toBehaviorContext(choice, hasOxygenData);

    const spec = useMemo(
        () =>
            buildTiltSpec({
                points: tiltDataPoints,
                threshold: tiltThreshold,
                context,
                t,
                locale: parseLocale(i18n.language),
            }),
        [tiltDataPoints, tiltThreshold, context, t, i18n.language]
    );

    return {
        chart: useMetricChart(spec),
        hasOxygenData,
        selectedRightAxisValue: context,
        setSelectedRightAxisValue: setChoice,
    };
}
